/**
 * DebtPlanSections — the ordered sections of the Liabilities tab.
 *
 * Order (set by the user): the four numbers, what you owe, your payoff path,
 * then everything that makes the plan work harder. Each section reads the one
 * shared plan from DebtPlanContext.
 */
import React from 'react';
import DebtPlanHero from './DebtPlanHero';
import DebtPriorityList from './DebtPriorityList';
import InterestCostCard from './InterestCostCard';
import PayoffOptions from './PayoffOptions';
import PayoffWinDialog from './PayoffWinDialog';
import { useDebtPlan } from './DebtPlanContext';
import {
  CardSkeleton, HeroSkeleton, SectionError, UpdatingChip, useLoadingPhase,
} from '@/components/common/SectionLoading';

export function SectionHeading({ title, subtitle, testid, tight, badge }) {
  return (
    <div className={`mb-4 ${tight ? 'mt-8' : 'mt-16 sm:mt-20'}`} data-testid={testid}>
      <h2 className="font-display text-2xl text-charcoal leading-tight">
        {title}{badge ? <> {badge}</> : null}
      </h2>
      {subtitle && <p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p>}
    </div>
  );
}

export function PayoffPathSection() {
  const { plan, busy, error, refreshing, retry } = useDebtPlan();
  const phase = useLoadingPhase(busy && !plan);
  if (!plan) {
    return (
      <>
        <SectionHeading title="Your payoff path" testid="payoff-path-heading" />
        {error
          ? <SectionError testid="debt-plan-error" onRetry={retry}
                          title="We couldn't load your debt plan just now." />
          : <HeroSkeleton phase={phase} testid="debt-plan-loading" minHeight={280}
                          label="Working out your payoff path"
                          message="Projecting your payoff date&hellip;" />}
      </>
    );
  }
  if (plan.state === 'no_debt') return null;

  return (
    <div data-testid="payoff-path-section">
      <SectionHeading
        title="Your payoff path"
        subtitle={plan.scope?.group_label
          ? `The plan created from your ${plan.scope.group_label} above.`
          : 'The plan created from the liabilities and payment details above.'}
        testid="payoff-path-heading"
        badge={<UpdatingChip show={refreshing} testid="debt-plan-updating" />}
      />
      <div aria-busy={refreshing}>
        <DebtPlanHero plan={plan} />
      </div>
    </div>
  );
}

export function PlanWorkHarderSection() {
  const { plan, extra, setExtra, busy, error } = useDebtPlan();
  const phase = useLoadingPhase(busy && !plan);
  if (!plan) {
    if (error) return null;              // the payoff section owns the retry
    return (
      <div data-testid="plan-work-harder-section">
        <SectionHeading
          title="Make the plan work harder"
          subtitle="See where an extra amount has the most effect, then compare a faster path."
          testid="plan-work-harder-heading"
        />
        <CardSkeleton phase={phase} testid="plan-work-harder-loading" rows={4}
                      label="Working out where extra money helps most" />
      </div>
    );
  }
  if (plan.state === 'no_debt') return null;
  const nothingToShow = !plan.payoff_path?.projectable && !plan.interest_cost?.available;
  if (nothingToShow) return null;

  return (
    <div data-testid="plan-work-harder-section">
      <SectionHeading
        title="Make the plan work harder"
        subtitle="See where an extra amount has the most effect, then compare a faster path."
        testid="plan-work-harder-heading"
      />
      <DebtPriorityList priorities={plan.priorities} />
      <PayoffOptions plan={plan} extra={extra} onExtraChange={setExtra} busy={busy} />
      <InterestCostCard interestCost={plan.interest_cost} priorities={plan.priorities} />
    </div>
  );
}

export function PayoffWinMount() {
  const { plan } = useDebtPlan();
  if (!plan) return null;
  return <PayoffWinDialog milestones={plan.milestones} />;
}
